import { FC, useState } from 'react'
import { useIntl } from 'react-intl'
import { Modal, Offcanvas } from 'react-bootstrap'
import { KTIcon } from '../../../_metronic/helpers'
import { KybDocument, KybStatusData, KybStatusValue, useKybStatus } from '../../hooks/useKybStatus'
import KybResubmitInline from './KybResubmitInline'
import { useAuth } from '../../modules/auth/core/Auth'

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

const formatEnum = (val: string | null | undefined): string =>
  val ? val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—'

// ── STEPPER ─────────────────────────────────────────────────────────────────

type StepState = 'done' | 'active' | 'pending' | 'rejected'

const StepCircle: FC<{ state: StepState; num: number }> = ({ state, num }) => {
  if (state === 'done')
    return (
      <div
        className='d-flex align-items-center justify-content-center rounded-circle bg-success'
        style={{ width: 48, height: 48, minWidth: 48 }}
      >
        <KTIcon iconName='check' className='fs-2 text-white' />
      </div>
    )
  if (state === 'active')
    return (
      <div
        className='d-flex align-items-center justify-content-center rounded-circle'
        style={{ width: 48, height: 48, minWidth: 48, border: '3px solid var(--bs-warning)' }}
      >
        <KTIcon iconName='time' className='fs-2 text-warning' />
      </div>
    )
  if (state === 'rejected')
    return (
      <div
        className='d-flex align-items-center justify-content-center rounded-circle bg-danger'
        style={{ width: 48, height: 48, minWidth: 48 }}
      >
        <KTIcon iconName='cross' className='fs-2 text-white' />
      </div>
    )
  return (
    <div
      className='d-flex align-items-center justify-content-center rounded-circle bg-light'
      style={{ width: 48, height: 48, minWidth: 48, border: '2px solid #D9D9D9' }}
    >
      <span className='fw-bold text-gray-400 fs-5'>{num}</span>
    </div>
  )
}

const StepperLine: FC<{ active: boolean }> = ({ active }) => (
  <div
    className='flex-grow-1'
    style={{ height: 4, marginTop: 22, borderRadius: 2, backgroundColor: active ? 'var(--bs-success)' : '#E4E6EF' }}
  />
)

const KybStepper: FC<{ kybData: KybStatusData | null | undefined }> = ({ kybData }) => {
  const intl = useIntl()
  const status = kybData?.status
  const isApproved = status === 'ACCEPTED'
  const isRejected = status === 'REJECTED'
  const step2State: StepState = isApproved || isRejected ? 'done' : 'active'
  const step3State: StepState = isApproved ? 'done' : isRejected ? 'rejected' : 'pending'
  const submittedDate = kybData?.created_at ? formatDate(kybData.created_at) : ''

  return (
    <div className='d-flex align-items-start w-100'>
      <div className='d-flex flex-column align-items-center' style={{ minWidth: 110 }}>
        <StepCircle state='done' num={1} />
        <div className='text-center mt-3'>
          <div className='fw-bold text-dark fs-7'>
            {intl.formatMessage({ id: 'KYB.REVIEW.STATUS_SUBMITTED', defaultMessage: 'Submitted' })}
          </div>
          {submittedDate && <div className='text-muted fs-8 mt-1'>{submittedDate}</div>}
        </div>
      </div>

      <StepperLine active={true} />

      <div className='d-flex flex-column align-items-center' style={{ minWidth: 140 }}>
        <StepCircle state={step2State} num={2} />
        <div className='text-center mt-3'>
          <div className={`fw-bold fs-7 ${step2State === 'active' ? 'text-warning' : 'text-dark'}`}>
            {intl.formatMessage({ id: 'KYB.REVIEW.STATUS_REVIEW', defaultMessage: 'Operator Review' })}
          </div>
          <div className={`fs-8 mt-1 ${step2State === 'active' ? 'text-warning' : 'text-muted'}`}>
            {step2State === 'active'
              ? intl.formatMessage({ id: 'KYB.REVIEW.STEP2_SUBLABEL_ACTIVE', defaultMessage: 'In review • TAT 24–48 hrs' })
              : intl.formatMessage({ id: 'KYB.REVIEW.STEP2_SUBLABEL_DONE', defaultMessage: 'Reviewed' })}
          </div>
        </div>
      </div>

      <StepperLine active={isApproved || isRejected} />

      <div className='d-flex flex-column align-items-center' style={{ minWidth: 110 }}>
        <StepCircle state={step3State} num={3} />
        <div className='text-center mt-3'>
          <div className={`fw-bold fs-7 ${isRejected ? 'text-danger' : 'text-dark'}`}>
            {intl.formatMessage({ id: 'KYB.REVIEW.STATUS_ACTIVE', defaultMessage: 'Approved / Rejected' })}
          </div>
          <div className={`fs-8 mt-1 ${isRejected ? 'text-danger' : 'text-muted'}`}>
            {step3State === 'pending'
              ? intl.formatMessage({ id: 'KYB.REVIEW.STEP3_SUBLABEL_PENDING', defaultMessage: 'Pending decision' })
              : isRejected
              ? intl.formatMessage({ id: 'KYB.REVIEW.STEP3_SUBLABEL_REJECTED', defaultMessage: 'Rejected — see reason below' })
              : intl.formatMessage({ id: 'KYB.REVIEW.STEP3_SUBLABEL_APPROVED', defaultMessage: 'Active' })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── REJECTION NOTICE ─────────────────────────────────────────────────────────

const KybRejectionNotice: FC<{ kybData: KybStatusData }> = ({ kybData }) => {
  const intl = useIntl()
  const [showResubmit, setShowResubmit] = useState(false)
  const decision = kybData.decision
  const attemptsUsed = kybData.resubmission_count
  const attemptsTotal = 3
  const isExhausted = attemptsUsed >= attemptsTotal

  return (
    <>
      <div className='card mt-6'>
        <div className='card-body p-6'>
          <div className='d-flex gap-4 align-items-start'>
            <div
              className='d-flex align-items-center justify-content-center rounded-circle bg-danger flex-shrink-0'
              style={{ width: 40, height: 40 }}
            >
              <KTIcon iconName='cross' className='fs-3 text-white' />
            </div>
            <div className='flex-grow-1'>
              <div className='fw-bold text-danger fs-6'>
                {intl.formatMessage({
                  id: 'KYB.REVIEW.REJECTION_NOTICE_TITLE',
                  defaultMessage: 'KYB rejected — please review and resubmit',
                })}
              </div>
              <div className='text-gray-700 fs-7 mt-1'>
                {intl.formatMessage({
                  id: 'KYB.REVIEW.REJECTION_NOTICE_BODY',
                  defaultMessage:
                    'Please review the reason below and resubmit with the same business registration number.',
                })}
              </div>
              {(decision?.decided_by_operator || decision?.reason) && (
                <div className='bg-light rounded p-5 mt-5'>
                  {decision?.decided_by_operator && (
                    <div className='fw-bold text-dark fs-7'>
                      {intl.formatMessage(
                        { id: 'KYB.REVIEW.REJECTED_BY', defaultMessage: 'Rejected by {name}' },
                        { name: decision.decided_by_operator }
                      )}
                    </div>
                  )}
                  {decision?.reason && (
                    <div className='text-gray-600 fs-7 mt-2'>{decision.reason}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='card mt-4'>
        <div className='card-body p-6'>
          <div className='d-flex align-items-center justify-content-between flex-wrap gap-4'>
            <div>
              <div className='fw-bold text-dark fs-6'>
                {intl.formatMessage({ id: 'KYB.REVIEW.RESUBMISSION_TITLE', defaultMessage: 'Resubmission attempts' })}
              </div>
              <div className='text-muted fs-7 mt-1'>
                {intl.formatMessage(
                  { id: 'KYB.REVIEW.RESUBMISSION_COUNT', defaultMessage: '{used} of {total} used' },
                  { used: attemptsUsed, total: attemptsTotal }
                )}
                {isExhausted && (
                  <span className='text-danger fw-bold ms-2'>
                    {intl.formatMessage({ id: 'KYB.REVIEW.NO_ATTEMPTS_LABEL', defaultMessage: '· No attempts left' })}
                  </span>
                )}
              </div>
              <div className='d-flex gap-2 mt-3'>
                {Array.from({ length: attemptsTotal }).map((_, i) => (
                  <div
                    key={i}
                    className='rounded'
                    style={{ width: 48, height: 6, backgroundColor: i < attemptsUsed ? 'var(--bs-danger)' : '#E4E6EF' }}
                  />
                ))}
              </div>
            </div>

            {!isExhausted && (
              <button
                type='button'
                className='btn btn-light-danger btn-sm d-flex align-items-center gap-2'
                onClick={() => setShowResubmit(true)}
              >
                <KTIcon iconName='arrows-circle' className='fs-4' />
                {intl.formatMessage({ id: 'KYB.REVIEW.RESUBMIT_BTN', defaultMessage: 'Resubmit KYB details' })}
              </button>
            )}
          </div>

          {isExhausted && (
            <div className='notice d-flex bg-light-danger rounded border-danger border border-dashed p-5 mt-5'>
              <div
                className='d-flex align-items-center justify-content-center rounded-circle bg-light-danger flex-shrink-0 me-4'
                style={{ width: 40, height: 40 }}
              >
                <KTIcon iconName='warning-2' className='fs-3 text-danger' />
              </div>
              <div>
                <div className='fw-bold text-danger fs-6'>
                  {intl.formatMessage({
                    id: 'KYB.REVIEW.MAX_RESUBMISSION_TITLE',
                    defaultMessage: 'Maximum resubmissions reached',
                  })}
                </div>
                <div className='text-gray-700 fs-7 mt-1'>
                  {intl.formatMessage({
                    id: 'KYB.REVIEW.MAX_RESUBMISSION_BODY',
                    defaultMessage:
                      'You have used all resubmission attempts. Please reach out to our support team so we can help you get KYB across the line.',
                  })}
                </div>
                <a href='mailto:support@goflipo.com' className='text-danger fw-bold fs-7 mt-2 d-block'>
                  support@goflipo.com
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {showResubmit && (
        <KybResubmitInline
          onHide={() => setShowResubmit(false)}
          prefill={kybData}
        />
      )}
    </>
  )
}

// ── DETAILS DRAWER ────────────────────────────────────────────────────────────

const DetailRow: FC<{ label: string; value: string | undefined }> = ({ label, value }) => {
  if (!value) return null
  return (
    <div className='d-flex flex-column gap-1'>
      <span className='text-muted fs-8 text-uppercase fw-semibold' style={{ letterSpacing: '0.5px' }}>
        {label}
      </span>
      <span className='text-dark fw-semibold fs-7'>{value}</span>
    </div>
  )
}

const KybDetailsDrawer: FC<{ show: boolean; onHide: () => void; kybData: KybStatusData }> = ({
  show,
  onHide,
  kybData,
}) => {
  const addressParts = [kybData.address_line1, kybData.address_line2].filter(Boolean).join(', ')
  const cityZip = [kybData.city, kybData.zip_code].filter(Boolean).join(' – ')

  return (
    <Offcanvas show={show} onHide={onHide} placement='end' style={{ width: 480 }}>
      <Offcanvas.Header className='border-bottom px-6 py-5'>
        <div className='d-flex align-items-center gap-3'>
          <KTIcon iconName='document' className='fs-2x text-primary' />
          <div>
            <h5 className='fw-bold text-dark mb-0'>Submitted sign-up details</h5>
            <div className='text-muted fs-8'>Business, authorised person and contact information</div>
          </div>
        </div>
        <button type='button' className='btn btn-sm btn-icon btn-light ms-auto' onClick={onHide}>
          <KTIcon iconName='cross' className='fs-2' />
        </button>
      </Offcanvas.Header>

      <Offcanvas.Body className='px-6 py-6' style={{ overflowY: 'auto' }}>
        <div className='d-flex flex-column gap-6'>
          <div>
            <div className='fw-bold text-dark fs-6 mb-4'>Business Information</div>
            <div className='d-flex flex-column gap-4'>
              <DetailRow label='Business Name' value={kybData.business_name} />
              <DetailRow label='Brand Name' value={kybData.brand_name} />
              <DetailRow label='Registration Number' value={kybData.registration_number} />
              <DetailRow label='Business Category' value={formatEnum(kybData.category)} />
              <DetailRow label='Resource Type' value={kybData.resource_type} />
            </div>
          </div>

          <div className='separator' />

          <div>
            <div className='fw-bold text-dark fs-6 mb-4'>Address</div>
            <div className='d-flex flex-column gap-4'>
              <DetailRow label='Address' value={addressParts} />
              <DetailRow label='City / ZIP' value={cityZip} />
              {kybData.state && <DetailRow label='State' value={kybData.state} />}
              <DetailRow label='Country' value={kybData.country} />
            </div>
          </div>

          <div className='separator' />

          <div>
            <div className='fw-bold text-dark fs-6 mb-4'>Authorised Person & Contact</div>
            <div className='d-flex flex-column gap-4'>
              <DetailRow label='Authorised Person' value={kybData.authorised_person_name} />
              <DetailRow label='Department' value={kybData.department} />
              <DetailRow label='Phone' value={kybData.contact_phone} />
              <DetailRow label='Email' value={kybData.email} />
            </div>
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  )
}

// ── DOCUMENTS MODAL ───────────────────────────────────────────────────────────

const DOC_TYPE_LABELS: Record<string, string> = {
  ENT_REGN_CERT: 'Entity Registration Certificate',
  TAX_INDT_NO: 'Tax Identification Number',
  VAT_REGN: 'VAT Registration',
}

const handleDownload = (url: string, filename: string) => {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.target = '_blank'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

const KybDocumentsModal: FC<{ show: boolean; onHide: () => void; documents: KybDocument[] }> = ({
  show,
  onHide,
  documents,
}) => (
  <Modal show={show} onHide={onHide} centered dialogClassName='mw-600px'>
    <Modal.Header className='border-0 pb-0 px-6 pt-6'>
      <div className='d-flex align-items-center gap-3'>
        <KTIcon iconName='folder' className='fs-2x text-primary' />
        <div>
          <h5 className='fw-bold text-dark mb-0'>Uploaded Documents</h5>
          <div className='text-muted fs-8'>{documents.length} document{documents.length !== 1 ? 's' : ''} submitted for KYB</div>
        </div>
      </div>
      <button type='button' className='btn btn-sm btn-icon btn-light ms-auto' onClick={onHide}>
        <KTIcon iconName='cross' className='fs-2' />
      </button>
    </Modal.Header>

    <Modal.Body className='px-6 pb-6 pt-4' style={{overflow: 'hidden'}}>
      <div className='d-flex flex-column gap-0'>
        {documents.map((doc, idx) => (
          <div key={doc.document_id}>
            {idx > 0 && <div className='separator my-4' />}
            <div className='d-flex align-items-center justify-content-between gap-4'>
              <div className='d-flex align-items-center gap-3 min-w-0'>
                <div
                  className='d-flex align-items-center justify-content-center rounded bg-light-primary flex-shrink-0'
                  style={{ width: 40, height: 40 }}
                >
                  <KTIcon iconName='file-down' className='fs-3 text-primary' />
                </div>
                <div className='min-w-0'>
                  <div className='fw-bold text-dark fs-7 text-truncate'>
                    {DOC_TYPE_LABELS[doc.document_type] ?? formatEnum(doc.document_type)}
                  </div>
                  <div className='text-muted fs-8' style={{wordBreak: 'break-all'}}>{doc.file_name}</div>
                </div>
              </div>
              <div className='d-flex gap-2 flex-shrink-0'>
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  onClick={() => window.open(doc.s3_object_uri, '_blank')}
                >
                  View
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-light'
                  onClick={() => handleDownload(doc.s3_object_uri, doc.file_name)}
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        ))}
        {documents.length === 0 && (
          <div className='text-center text-muted py-6 fs-7'>No documents found.</div>
        )}
      </div>
    </Modal.Body>
  </Modal>
)

// ── INFO CARDS ────────────────────────────────────────────────────────────────

const KybInfoCards: FC<{ onViewDetails: () => void; onViewDocuments: () => void }> = ({
  onViewDetails,
  onViewDocuments,
}) => {
  const intl = useIntl()
  return (
    <div className='row g-5 mt-2'>
      <div className='col-12 col-md-6'>
        <div className='card h-100'>
          <div className='card-body p-6 d-flex flex-column gap-5'>
            <div className='d-flex align-items-start gap-4'>
              <div
                className='d-flex align-items-center justify-content-center rounded bg-light-primary flex-shrink-0'
                style={{ width: 48, height: 48 }}
              >
                <KTIcon iconName='document' className='fs-2x text-primary' />
              </div>
              <div>
                <div className='fw-bold text-dark fs-6'>
                  {intl.formatMessage({
                    id: 'KYB.REVIEW.SIGNUP_DETAILS_TITLE',
                    defaultMessage: 'Submitted sign-up details',
                  })}
                </div>
                <div className='text-muted fs-7 mt-1'>
                  {intl.formatMessage({
                    id: 'KYB.REVIEW.SIGNUP_DETAILS_DESC',
                    defaultMessage: 'Business, authorised person and contact information.',
                  })}
                </div>
              </div>
            </div>
            <button type='button' className='btn btn-sm btn-light-primary align-self-start' onClick={onViewDetails}>
              {intl.formatMessage({ id: 'KYB.REVIEW.VIEW_DETAILS', defaultMessage: 'View details' })}
            </button>
          </div>
        </div>
      </div>

      <div className='col-12 col-md-6'>
        <div className='card h-100'>
          <div className='card-body p-6 d-flex flex-column gap-5'>
            <div className='d-flex align-items-start gap-4'>
              <div
                className='d-flex align-items-center justify-content-center rounded bg-light-primary flex-shrink-0'
                style={{ width: 48, height: 48 }}
              >
                <KTIcon iconName='folder' className='fs-2x text-primary' />
              </div>
              <div>
                <div className='fw-bold text-dark fs-6'>
                  {intl.formatMessage({ id: 'KYB.REVIEW.DOCUMENTS_TITLE', defaultMessage: 'Uploaded documents' })}
                </div>
                <div className='text-muted fs-7 mt-1'>
                  {intl.formatMessage({
                    id: 'KYB.REVIEW.DOCUMENTS_DESC',
                    defaultMessage: 'Documents submitted for KYB. View or download.',
                  })}
                </div>
              </div>
            </div>
            <button type='button' className='btn btn-sm btn-light-primary align-self-start' onClick={onViewDocuments}>
              {intl.formatMessage({ id: 'KYB.REVIEW.VIEW_DOCUMENTS', defaultMessage: 'View documents' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── HELP SECTION ──────────────────────────────────────────────────────────────

const KybHelpSection: FC = () => {
  const intl = useIntl()
  return (
    <div className='card mt-4'>
      <div className='card-body p-6'>
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-4'>
          <div className='d-flex align-items-center gap-4'>
            <div
              className='d-flex align-items-center justify-content-center rounded bg-light-primary flex-shrink-0'
              style={{ width: 48, height: 48 }}
            >
              <KTIcon iconName='message-question' className='fs-2x text-primary' />
            </div>
            <div>
              <div className='fw-bold text-dark fs-6'>
                {intl.formatMessage({
                  id: 'KYB.REVIEW.HELP_TITLE',
                  defaultMessage: 'Need help with submission or resubmission?',
                })}
              </div>
              <div className='text-muted fs-7 mt-1'>
                {intl.formatMessage({
                  id: 'KYB.REVIEW.HELP_BODY',
                  defaultMessage:
                    'If you are unable to submit or resubmit your KYB details, drop us an email and we will assist you.',
                })}
              </div>
            </div>
          </div>
          <a href='mailto:support@goflipo.com' className='d-flex align-items-center gap-2 text-primary fw-bold fs-7'>
            <KTIcon iconName='sms' className='fs-4 text-primary' />
            support@goflipo.com
          </a>
        </div>
      </div>
    </div>
  )
}

// ── APPROVED NOTICE ───────────────────────────────────────────────────────────

const KybApprovedNotice: FC<{ accountId?: string }> = ({ accountId }) => {
  const intl = useIntl()
  const { logout } = useAuth()

  return (
    <div className='card mt-6'>
      <div className='card-body p-8'>
        <div className='d-flex align-items-start gap-6 flex-wrap flex-md-nowrap'>
          <div
            className='d-flex align-items-center justify-content-center rounded-circle bg-light-success flex-shrink-0'
            style={{ width: 72, height: 72 }}
          >
            <KTIcon iconName='shield-tick' className='fs-2tx text-success' />
          </div>

          <div className='flex-grow-1'>
            <div className='fw-bold text-dark fs-2 mb-2'>
              {intl.formatMessage({
                id: 'KYB.REVIEW.APPROVED_TITLE',
                defaultMessage: 'KYB Approved — Your account is ready!',
              })}
            </div>
            <div className='text-gray-600 fs-6 mb-4'>
              {intl.formatMessage({
                id: 'KYB.REVIEW.APPROVED_DESC',
                defaultMessage:
                  'Your Know Your Business verification has been completed successfully. To access your full Telemarketer dashboard, please log out and sign back in with your TM ID.',
              })}
            </div>

            {accountId && (
              <div className='bg-light-success border border-success border-opacity-25 rounded-2 px-5 py-3 mb-6 d-inline-flex align-items-center gap-3'>
                <KTIcon iconName='key' className='fs-3 text-success' />
                <div>
                  <div className='fs-8 text-muted fw-semibold text-uppercase'>
                    {intl.formatMessage({ id: 'KYB.REVIEW.APPROVED_TM_ID_LABEL', defaultMessage: 'Your TM ID' })}
                  </div>
                  <div className='fs-5 fw-bold text-dark'>{accountId}</div>
                </div>
              </div>
            )}

            <div className='d-flex flex-column gap-3 mb-8'>
              {[
                intl.formatMessage({ id: 'KYB.REVIEW.APPROVED_STEP1', defaultMessage: 'Click "Log out now" below' }),
                intl.formatMessage({ id: 'KYB.REVIEW.APPROVED_STEP2', defaultMessage: 'Sign in with your TM ID and credentials' }),
                intl.formatMessage({ id: 'KYB.REVIEW.APPROVED_STEP3', defaultMessage: 'Access your full TM dashboard' }),
              ].map((step, i) => (
                <div key={i} className='d-flex align-items-center gap-3'>
                  <div
                    className='d-flex align-items-center justify-content-center rounded-circle bg-success flex-shrink-0'
                    style={{ width: 28, height: 28 }}
                  >
                    <span className='text-white fw-bold fs-8'>{i + 1}</span>
                  </div>
                  <span className='text-gray-700 fs-6'>{step}</span>
                </div>
              ))}
            </div>

            <button
              type='button'
              className='btn btn-success d-flex align-items-center gap-2'
              onClick={logout}
            >
              <KTIcon iconName='entrance-right' className='fs-4' />
              {intl.formatMessage({ id: 'KYB.REVIEW.APPROVED_LOGOUT_BTN', defaultMessage: 'Log out now' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── PAGE HEADER HELPERS ───────────────────────────────────────────────────────

const statusTitle = (status: KybStatusValue | undefined, intl: ReturnType<typeof useIntl>): string => {
  if (status === 'ACCEPTED')
    return intl.formatMessage({ id: 'KYB.REVIEW.TITLE_APPROVED', defaultMessage: 'KYB Accepted' })
  if (status === 'REJECTED')
    return intl.formatMessage({ id: 'KYB.REVIEW.TITLE_REJECTED', defaultMessage: 'KYB Rejected — Action Required' })
  return intl.formatMessage({ id: 'KYB.REVIEW.TITLE_PENDING', defaultMessage: 'KYB Review in Progress' })
}

const statusBadge = (
  status: KybStatusValue | undefined,
  intl: ReturnType<typeof useIntl>
): { label: string; className: string; icon: string } => {
  if (status === 'ACCEPTED')
    return {
      label: intl.formatMessage({ id: 'KYB.REVIEW.BADGE_APPROVED', defaultMessage: 'Accepted' }),
      className: 'bg-light-success text-success',
      icon: 'shield-tick',
    }
  if (status === 'REJECTED')
    return {
      label: intl.formatMessage({ id: 'KYB.REVIEW.BADGE_REJECTED', defaultMessage: 'Rejected' }),
      className: 'bg-light-danger text-danger',
      icon: 'cross-circle',
    }
  return {
    label: intl.formatMessage({ id: 'KYB.REVIEW.BADGE_PENDING', defaultMessage: 'Pending' }),
    className: 'bg-light-warning text-warning',
    icon: 'time',
  }
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────

const KybReviewPage: FC = () => {
  const intl = useIntl()
  const { data: kybData, isLoading } = useKybStatus()
  const [showDetails, setShowDetails] = useState(false)
  const [showDocs, setShowDocs] = useState(false)

  if (isLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center py-20'>
        <div className='spinner-border text-primary' style={{ width: 40, height: 40 }} role='status' />
      </div>
    )
  }

  const status = kybData?.status
  const isRejected = status === 'REJECTED'
  const isApproved = status === 'ACCEPTED'
  const submittedDate = kybData?.created_at ? formatDate(kybData.created_at) : ''
  const title = statusTitle(status, intl)
  const badge = statusBadge(status, intl)

  const headerIconColor = isRejected ? 'text-danger' : isApproved ? 'text-success' : 'text-primary'

  return (
    <div className='py-4'>
      <div className='mb-6'>
        <div className='d-flex align-items-center gap-3 flex-wrap'>
          <KTIcon iconName='shield-tick' className={`fs-2x ${headerIconColor}`} />
          <h2 className='fw-bold text-dark mb-0 fs-2'>{title}</h2>
          <span className={`badge d-flex align-items-center gap-1 px-4 py-2 ${badge.className}`}>
            <KTIcon iconName={badge.icon} className='fs-7' />
            {badge.label}
          </span>
        </div>
        {submittedDate && (
          <div className='text-muted fs-7 mt-2 ms-1'>
            {intl.formatMessage(
              { id: 'KYB.REVIEW.SUBMITTED_ON', defaultMessage: 'Submitted on {date}' },
              { date: submittedDate }
            )}
          </div>
        )}
      </div>

      <div className='card'>
        <div className='card-body py-8 px-8'>
          <KybStepper kybData={kybData} />
        </div>
      </div>

      {isApproved && <KybApprovedNotice accountId={kybData?.account_id} />}
      {isRejected && kybData && <KybRejectionNotice kybData={kybData} />}

      <KybInfoCards
        onViewDetails={() => setShowDetails(true)}
        onViewDocuments={() => setShowDocs(true)}
      />
      {!isApproved && <KybHelpSection />}

      {kybData && (
        <>
          <KybDetailsDrawer show={showDetails} onHide={() => setShowDetails(false)} kybData={kybData} />
          <KybDocumentsModal
            show={showDocs}
            onHide={() => setShowDocs(false)}
            documents={kybData.documents ?? []}
          />
        </>
      )}
    </div>
  )
}

export default KybReviewPage
